import { useState, useEffect, useCallback } from 'react';
import { companyAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

export interface RecipeIngredient {
  id?: number;
  ingredient: number | null;
  starter: number | null;
  name_override: string;
  display_name: string;
  quantity: string;
  unit: string;
  ingredient_unit: string;
  notes: string;
}

export interface Recipe {
  id: number;
  name: string;
  recipe_type: string;
  recipe_type_display: string;
  total_yield_g: string | null;
  yield_quantity: number | null;
  yield_unit: string;
  description: string;
  process: string;
  recipe_ingredients: RecipeIngredient[];
  created_at: string;
  updated_at: string;
}

export const RECIPE_TYPE_OPTIONS = [
  { value: 'bread', label: 'Bread' },
  { value: 'pastry', label: 'Pastry' },
  { value: 'cake', label: 'Cake' },
  { value: 'cookie', label: 'Cookie' },
  { value: 'pizza', label: 'Pizza' },
  { value: 'cracker', label: 'Cracker' },
  { value: 'other', label: 'Other' },
];

const emptyForm = {
  name: '',
  recipe_type: 'bread',
  total_yield_g: '',
  yield_quantity: '',
  yield_unit: 'loaf',
  description: '',
  process: '',
};

export type RecipeForm = typeof emptyForm;

const emptyLine = (): RecipeIngredient => ({
  ingredient: null, starter: null, name_override: '',
  display_name: '', quantity: '', unit: '', ingredient_unit: '', notes: '',
});

export function useCompanyRecipes() {
  const { isExpired } = useAuth();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [allIngredients, setAllIngredients] = useState<any[]>([]);
  const [allStarters, setAllStarters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [feedback, setFeedback] = useState({ error: '', success: '' });

  const [showModal, setShowModal] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);
  const [form, setForm] = useState<RecipeForm>(emptyForm);
  const [lines, setLines] = useState<RecipeIngredient[]>([]);
  const [saving, setSaving] = useState(false);

  const [deleteState, setDeleteState] = useState<{
    showModal: boolean; recipe: Recipe | null; deleting: boolean;
  }>({ showModal: false, recipe: null, deleting: false });

  const fetchData = useCallback(async () => {
    try {
      const [recipesRes, ingredientsRes, startersRes] = await Promise.all([
        companyAPI.getRecipes(),
        companyAPI.getIngredients(),
        companyAPI.getStarters(),
      ]);
      setRecipes(recipesRes.data);
      setAllIngredients(Array.isArray(ingredientsRes.data) ? ingredientsRes.data : []);
      setAllStarters(Array.isArray(startersRes.data) ? startersRes.data : []);
    } catch (err) {
      console.error('Failed to fetch recipes data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = recipes.filter(r => {
    const matchSearch = r.name.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === 'all' || r.recipe_type === typeFilter;
    return matchSearch && matchType;
  });

  const handleOpenCreate = () => {
    setEditingRecipe(null);
    setForm(emptyForm);
    setLines([]);
    setFeedback({ error: '', success: '' });
    setShowModal(true);
  };

  const handleOpenEdit = (recipe: Recipe) => {
    setEditingRecipe(recipe);
    setForm({
      name: recipe.name,
      recipe_type: recipe.recipe_type,
      total_yield_g: recipe.total_yield_g ?? '',
      yield_quantity: (recipe as any).yield_quantity ?? '',
      yield_unit: (recipe as any).yield_unit ?? 'loaf',
      description: recipe.description,
      process: recipe.process,
    });
    setLines(recipe.recipe_ingredients.map(ri => ({ ...ri, quantity: String(ri.quantity) })));
    setFeedback({ error: '', success: '' });
    setShowModal(true);
  };

  const addIngredientLine = () => setLines(prev => [...prev, emptyLine()]);

  const addStarterLine = () => setLines(prev => [...prev, {
    ...emptyLine(),
    unit: 'g',
    ingredient_unit: '__starter__',
  }]);

  const removeLine = (idx: number) => setLines(prev => prev.filter((_, i) => i !== idx));

  const updateLine = (idx: number, field: keyof RecipeIngredient, value: any) => {
    setLines(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      // Auto-fill unit from ingredient
      if (field === 'ingredient' && value) {
        const ing = allIngredients.find((i: any) => i.id === parseInt(value));
        if (ing) {
          updated[idx].ingredient_unit = ing.unit;
          if (!updated[idx].unit) updated[idx].unit = ing.unit;
          updated[idx].display_name = ing.name;
          updated[idx].starter = null;
        }
      }
      if (field === 'starter' && value) {
        const st = allStarters.find((s: any) => s.id === parseInt(value));
        if (st) {
          updated[idx].display_name = st.name;
          updated[idx].ingredient = null;
          updated[idx].ingredient_unit = '';
          if (!updated[idx].unit) updated[idx].unit = 'g';
        }
      }
      return updated;
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFeedback({ error: 'Recipe name is required.', success: '' });
      return;
    }
    setSaving(true);
    setFeedback({ error: '', success: '' });
    const payload: any = {
      name: form.name.trim(),
      recipe_type: form.recipe_type,
      total_yield_g: form.total_yield_g !== '' ? form.total_yield_g : null,
      yield_quantity: (form as any).yield_quantity !== '' ? (form as any).yield_quantity : null,
      yield_unit: (form as any).yield_unit,
      description: form.description,
      process: form.process,
      recipe_ingredients: lines.map(l => ({
        ingredient: l.ingredient || null,
        starter: l.starter || null,
        name_override: l.name_override,
        quantity: l.quantity,
        unit: l.unit,
        notes: l.notes,
      })),
    };
    try {
      if (editingRecipe) {
        const res = await companyAPI.updateRecipe(editingRecipe.id, payload);
        setRecipes(prev => prev.map(r => r.id === editingRecipe.id ? res.data : r));
      } else {
        const res = await companyAPI.createRecipe(payload);
        setRecipes(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      }
      setShowModal(false);
      setFeedback({ error: '', success: editingRecipe ? 'Recipe updated.' : 'Recipe created.' });
    } catch (err: any) {
      const msg = err?.response?.data?.name?.[0] || err?.response?.data?.detail || 'Failed to save recipe.';
      setFeedback({ error: msg, success: '' });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = (recipe: Recipe) => {
    setDeleteState({ showModal: true, recipe, deleting: false });
  };

  const handleDelete = async () => {
    if (!deleteState.recipe) return;
    setDeleteState(prev => ({ ...prev, deleting: true }));
    try {
      await companyAPI.deleteRecipe(deleteState.recipe.id);
      setRecipes(prev => prev.filter(r => r.id !== deleteState.recipe!.id));
      setDeleteState({ showModal: false, recipe: null, deleting: false });
      setFeedback({ error: '', success: 'Recipe deleted.' });
    } catch {
      setDeleteState(prev => ({ ...prev, deleting: false }));
      setFeedback({ error: 'Failed to delete recipe.', success: '' });
    }
  };

  return {
    isExpired,
    recipes,
    allIngredients,
    allStarters,
    loading,
    search,
    setSearch,
    typeFilter,
    setTypeFilter,
    filtered,
    showModal,
    editingRecipe,
    form,
    setForm,
    lines,
    saving,
    feedback,
    setFeedback,
    deleteState,
    handleOpenCreate,
    handleOpenEdit,
    addIngredientLine,
    addStarterLine,
    removeLine,
    updateLine,
    handleSave,
    handleConfirmDelete,
    handleDelete,
    closeModal: () => setShowModal(false),
    closeDeleteModal: () => setDeleteState({ showModal: false, recipe: null, deleting: false }),
  };
}
